import axios from 'axios';
import { UPDATE_BILL_URL } from '../constant/apiConstants.js';

/**
 * updateBillApi
 * ----------------------------------------------------------------------------
 * Re-saves a FRESH retail bill inside its 20-minute edit window and returns the
 * reprint, in the same shape as createBillApi.
 *
 * Body: the same BillCreationModel as createBillApi, plus the bill to replace:
 *   {
 *     billNo, fy, storeId,
 *     user, token,
 *     customerName, customerMobileNo, paymentMode, schoolName,
 *     globalDiscount,
 *     itemPurchaseList: [ItemPurchaseModel],
 *     itemReturnList:   [],                  // returns/exchanges are rejected
 *     transactionModel: { cash, upi, card }  // only read for 'Partial'
 *   }
 *
 * The server replaces every line, payment and stock movement of the bill.
 * It answers HTTP 400 with an ApiResponseModel ({ status, payload, message })
 * when the bill is not FRESH, holds return/exchange lines, or is older than
 * 20 minutes — read that message with getUpdateBillErrorMessage().
 *
 * @param {Object} params
 * @param {Object} params.billCreationModel  Full request body (see above).
 * @param {string} [params.printType='PDF']  'THERMAL' | 'PDF' | ... (case-insensitive).
 * @param {string} [params.billSendType='PRINT'] 'PRINT' | 'WHATSAPP' (case-insensitive).
 * @returns {Promise<{ isThermal: boolean, data: (object|ArrayBuffer), raw: import('axios').AxiosResponse }>}
 * @throws  Propagates any axios/network error to the caller.
 */
export const updateBillApi = async ({
  billCreationModel,
  printType = 'PDF',
  billSendType = 'PRINT',
}) => {
  const normalizedPrintType = (printType || 'PDF').trim().toUpperCase();
  const normalizedSendType = (billSendType || 'PRINT').trim().toUpperCase();
  const isThermal = normalizedPrintType === 'THERMAL';

  // Thermal -> JSON print payload, everything else -> raw PDF bytes.
  const responseType = isThermal ? 'json' : 'arraybuffer';

  const url =
    `${UPDATE_BILL_URL}?printType=${normalizedPrintType}` +
    `&billSendType=${normalizedSendType}`;

  const response = await axios.post(url, billCreationModel, { responseType });

  return {
    isThermal,
    data: response.data,
    raw: response,
  };
};

/**
 * Reads the backend's ApiResponseModel.message from a failed update.
 * When a PDF was requested the error body arrives as an ArrayBuffer, so it is
 * decoded before parsing.
 */
export const getUpdateBillErrorMessage = (error, fallback = 'Unable to update bill') => {
  const data = error?.response?.data;
  if (!data) {
    return error?.message ? `${fallback} (${error.message})` : fallback;
  }

  try {
    const parsed = data instanceof ArrayBuffer
      ? JSON.parse(new TextDecoder('utf-8').decode(data))
      : data;
    if (typeof parsed === 'string') return parsed || fallback;
    return parsed?.message || fallback;
  } catch {
    return fallback;
  }
};

export default updateBillApi;
